import React from 'react';
import { StyleSheet } from 'react-native';
import Svg, { Circle } from 'react-native-svg';

import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { PlayerAvatar } from '@/components/player-avatar/PlayerAvatar';
import { EntryItemPick } from '@/feature/entries-pickem/components/EntriesList';
import { pickStatusBadgeMap } from '@/feature/entries-pickem/components/pick-status-icons';
import { designSystem, withOpacity } from '@/styles/styles';
import { EventStatus, ProjectionType } from '@/types/api.generated';
import { PICK_STATUS_CONFIG, PickStatus, getPickStatus, isPickInProgress } from '@/utils/pick-status';

type PickCircleWithStatusProps = {
    pick: EntryItemPick;
};

export const CIRCLE_SIZE = 40;
const BORDER_WIDTH = 2;
export const PickCircleWithStatus = ({ pick }: PickCircleWithStatusProps) => {
    const status = getPickStatus(pick);
    const isLiveEvent = pick.eventStatus === EventStatus.InProgress;
    const isLiveEventWithPendingResult = isLiveEvent && !pick.result;
    const isTrackedLiveEventWithPendingResult = isLiveEventWithPendingResult && !pick.projection.liveScoringDisabled;

    const max =
        pick.projection.type !== ProjectionType.Regular && pick.projection.nonRegularValue
            ? pick.projection.nonRegularValue
            : pick.projection.value;
    const progress = isTrackedLiveEventWithPendingResult ? (pick.projection.currentValue || 0) / (max || Infinity) : 0;
    const isUpcoming = status === PickStatus.UPCOMING;
    // Figma specifies 40px for upcoming picks (no status badge) and 45px when a status badge is
    // shown. The extra 5px lets the badge peek below the avatar circle.
    const height = isUpcoming ? 40 : 45;

    return (
        <Box height={height} accessibilityRole={'image'} accessibilityLabel={'Player image with status: ' + status}>
            <Box
                backgroundColor={ds.colorToken.background.neutral.subtle}
                width={CIRCLE_SIZE}
                height={CIRCLE_SIZE}
                borderRadius={'r40'}
                borderWidth={BORDER_WIDTH}
                borderColor={PICK_STATUS_CONFIG[status].avatarRingColor}
                alignItems={'center'}
                justifyContent={'center'}
                style={styles.shadow}
            >
                <PlayerAvatar playerData={pick} playerAvatarVariant={'entryPick'} />
                <Box zIndex={-1} position={'absolute'}>
                    <CircularProgress progress={progress} />
                </Box>
            </Box>
            {!isUpcoming ? <StatusCircle status={status} /> : null}
        </Box>
    );
};

const CircularProgress = ({ progress }: { progress: number }) => {
    const size = CIRCLE_SIZE;

    const clampedProgress = Math.min(Math.max(progress, 0), 1);

    const radius = (size - BORDER_WIDTH) / 2;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - clampedProgress * circumference;

    return (
        <Svg width={size} height={size}>
            <Circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                stroke={ds.colorValue.icon.primary}
                strokeWidth={BORDER_WIDTH}
                fill="none"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                strokeLinecap="round"
                // rotate so that the progress starts from the bottom (6 o'clock)
                transform={`rotate(90, ${size / 2}, ${size / 2})`}
            />
        </Svg>
    );
};

const StatusCircle = ({ status }: { status: PickStatus }) => {
    return (
        <Box
            backgroundColor={PICK_STATUS_CONFIG[status].badgeColor}
            width={16}
            height={16}
            borderRadius={isPickInProgress(status) ? 'r6' : 'r16'}
            position={'absolute'}
            bottom={0}
            left={12}
            alignItems={'center'}
            justifyContent={'center'}
        >
            {pickStatusBadgeMap[status]}
        </Box>
    );
};

const styles = StyleSheet.create({
    shadow: {
        shadowOffset: {
            height: 3,
            width: 0,
        },
        shadowRadius: 4,
        shadowOpacity: 0.7,
        shadowColor: withOpacity(designSystem.colors.neutral900, 0.7),
        elevation: 4,
    },
});
